import { useCallback, useMemo } from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { useQuery as UseQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql, readFragment } from '~publish/graphql'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'
import { client } from '~publish/legacy/apollo-client'

import {
  refreshConnectionTotalCount,
  refreshCountIfPagesUnloaded,
  useDebouncedTask,
} from './connectionRefresh'
import { ContentItemCard_ContentItem } from './fragments/ContentItemCard_ContentItem'
import type {
  ContentItemUpsertEventType,
  RealTimeContentItem,
} from './useContentItemRealTimePusherUpdates'
import { useContentItemRealTimePusherUpdates } from './useContentItemRealTimePusherUpdates'
import type { ContentItemCard_ContentItemData } from './fragments/ContentItemCard_ContentItem'

export const CONTENT_ITEMS_PER_PAGE = 20

export const GetContentItemList = graphql(
  `
    query GetContentItemList(
      $first: Int!
      $after: String
      $organizationId: OrganizationId!
      $sort: [ContentItemsSortInput!]
    ) {
      contentItems(
        first: $first
        after: $after
        input: {
          organizationId: $organizationId
          filter: { contentStatus: draftContent }
          sort: $sort
        }
      ) {
        edges {
          node {
            id
            ...ContentItemCard_ContentItem
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        totalCount
      }
    }
  `,
  [ContentItemCard_ContentItem],
)

type GetContentItemListData = ResultOf<typeof GetContentItemList>
type GetContentItemListVariables = VariablesOf<typeof GetContentItemList>

export type ContentItemDraft = ContentItemCard_ContentItemData
export type ContentItemSortInput = NonNullable<
  GetContentItemListVariables['sort']
>[number]

type UsePaginatedContentItemsOptions = {
  perPage?: number
  skip?: boolean
  /** Defaults to the server's newest-first order when omitted. */
  sort?: ContentItemSortInput[]
}

type UsePaginatedContentItemsResult = {
  /** Content items whose body is a channel-less `DraftContent`. */
  draftContentItems: ContentItemDraft[]
  totalCount: number
  loading: boolean
  fetchingMore: boolean
  hasNextPage: boolean
  error: UseQuery.Result<
    GetContentItemListData,
    GetContentItemListVariables
  >['error']
  lastElementRef: UseIntersectionObserverReturn[0]
  refetch: UseQuery.Result<
    GetContentItemListData,
    GetContentItemListVariables
  >['refetch']
}

/**
 * Paginated list of an organization's channel-less drafts, mirroring
 * usePaginatedPostList. The `contentItems` query filters to `draftContent`
 * server-side, so every returned item is a draft and the connection's
 * pageInfo/totalCount describe the draft set directly.
 */
export function usePaginatedContentItems({
  perPage = CONTENT_ITEMS_PER_PAGE,
  skip = false,
  sort,
}: UsePaginatedContentItemsOptions = {}): UsePaginatedContentItemsResult {
  const organizationId = useOrganizationId()

  const { data, refetch, fetchMore, error, networkStatus, variables } =
    useQuery(GetContentItemList, {
      variables: {
        first: perPage,
        after: null,
        organizationId: organizationId || '',
        sort,
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      skip: skip || !organizationId,
    })

  const isSkipped = skip || !organizationId
  const scheduleCountRefresh = useDebouncedTask(
    () => refreshConnectionTotalCount(GetContentItemList, variables),
    'content item list count refresh',
  )

  useContentItemRealTimePusherUpdates({
    skip: isSkipped,
    onItemFetched: (item, eventType) =>
      upsertContentItemInList(
        variables,
        item,
        sort,
        eventType,
        scheduleCountRefresh,
      ),
    onDeleted: (id) =>
      removeContentItemFromList(variables, id, scheduleCountRefresh),
  })

  const loading = networkStatus === NetworkStatus.loading
  const fetchingMore = networkStatus === NetworkStatus.fetchMore
  const hasNextPage = data?.contentItems.pageInfo.hasNextPage ?? false
  const endCursor = data?.contentItems.pageInfo.endCursor

  const loadMore = useCallback(async () => {
    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.contentItems.edges) return prev

          const newEdges = fetchMoreResult.contentItems.edges
          const newIds = new Set(newEdges.map((edge) => edge.node.id))
          const existingEdges = (prev.contentItems.edges ?? []).filter(
            (edge) => !newIds.has(edge.node.id),
          )

          return {
            ...prev,
            contentItems: {
              ...prev.contentItems,
              edges: [...existingEdges, ...newEdges],
              pageInfo: fetchMoreResult.contentItems.pageInfo,
            },
          }
        },
      })
    } catch (caughtError) {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Unknown error'
      toast.error('Error fetching more drafts')
      throw new Error(`Failed to fetch more content items: ${message}`, {
        cause: caughtError,
      })
    }
  }, [fetchMore, endCursor])

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage,
    fetchMore: loadMore,
  })

  const draftContentItems = useMemo(() => {
    const nodes = (data?.contentItems.edges ?? []).map((edge) => edge.node)
    return [...readFragment(ContentItemCard_ContentItem, nodes)]
  }, [data?.contentItems.edges])

  return useMemo(
    () => ({
      draftContentItems,
      totalCount: data?.contentItems.totalCount ?? 0,
      loading,
      fetchingMore,
      hasNextPage,
      error,
      lastElementRef,
      refetch,
    }),
    [
      draftContentItems,
      data?.contentItems.totalCount,
      loading,
      fetchingMore,
      hasNextPage,
      error,
      lastElementRef,
      refetch,
    ],
  )
}

/**
 * Inserts, updates, or drops a content item in one `GetContentItemList` cache
 * entry. Each distinct `variables` has its own entry.
 */
function upsertContentItemInList(
  variables: GetContentItemListVariables | undefined,
  item: RealTimeContentItem,
  sort: ContentItemSortInput[] | undefined,
  eventType: ContentItemUpsertEventType,
  scheduleCountRefresh: () => void,
): void {
  // No caller passes a custom sort, and a guessed position under one could
  // be wrong, so the next refetch settles that case.
  if (sort?.length) {
    return
  }

  const existingData = client.cache.readQuery<GetContentItemListData>({
    query: GetContentItemList,
    variables,
  })
  if (!existingData?.contentItems.edges) {
    return
  }

  const wasPresent = existingData.contentItems.edges.some(
    (edge) => edge.node.id === item.id,
  )
  const remainingEdges = existingData.contentItems.edges.filter(
    (edge) => edge.node.id !== item.id,
  )

  // An item whose body is no longer `DraftContent` (for example, promoted
  // to a channel post) has left this connection.
  const unmaskedItem = readFragment(ContentItemCard_ContentItem, item)
  if (!isOfType(unmaskedItem.body, 'DraftContent')) {
    if (!wasPresent) {
      refreshCountIfPagesUnloaded(
        existingData.contentItems,
        scheduleCountRefresh,
      )
      return
    }
    client.cache.writeQuery({
      query: GetContentItemList,
      variables,
      data: {
        ...existingData,
        contentItems: {
          ...existingData.contentItems,
          edges: remainingEdges,
          totalCount: existingData.contentItems.totalCount - 1,
        },
      },
    })
    return
  }

  // The list sorts newest-created first, so an update keeps the item's
  // place. An update to an item on an unloaded page is already counted, and
  // its place is unknown, so only a creation goes in at the head.
  if (wasPresent) {
    client.cache.writeQuery({
      query: GetContentItemList,
      variables,
      data: {
        ...existingData,
        contentItems: {
          ...existingData.contentItems,
          edges: existingData.contentItems.edges.map((edge) =>
            edge.node.id === item.id ? { ...edge, node: item } : edge,
          ),
        },
      },
    })
    return
  }
  if (eventType === 'updated') {
    return
  }

  client.cache.writeQuery({
    query: GetContentItemList,
    variables,
    data: {
      ...existingData,
      contentItems: {
        ...existingData.contentItems,
        edges: [
          { __typename: 'ContentItemEdge' as const, node: item },
          ...remainingEdges,
        ],
        totalCount: existingData.contentItems.totalCount + 1,
      },
    },
  })
}

/**
 * Drops a content item from one `GetContentItemList` cache entry after a
 * delete event, adjusting `totalCount`.
 */
function removeContentItemFromList(
  variables: GetContentItemListVariables | undefined,
  id: string,
  scheduleCountRefresh: () => void,
): void {
  const existingData = client.cache.readQuery<GetContentItemListData>({
    query: GetContentItemList,
    variables,
  })
  if (!existingData?.contentItems.edges) {
    return
  }

  const wasPresent = existingData.contentItems.edges.some(
    (edge) => edge.node.id === id,
  )
  if (!wasPresent) {
    refreshCountIfPagesUnloaded(existingData.contentItems, scheduleCountRefresh)
    return
  }

  client.cache.writeQuery({
    query: GetContentItemList,
    variables,
    data: {
      ...existingData,
      contentItems: {
        ...existingData.contentItems,
        edges: existingData.contentItems.edges.filter(
          (edge) => edge.node.id !== id,
        ),
        totalCount: existingData.contentItems.totalCount - 1,
      },
    },
  })
}
